import { Close } from "@mui/icons-material";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Dialog as MuiDialog,
  type DialogProps as MuiDialogProps,
  type SxProps,
  type Theme,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";

const VISUALLY_HIDDEN_SX: SxProps<Theme> = {
  position: "absolute",
  // Use explicit px strings: MUI's sx sizing system treats numeric width/height <= 1 as percentages.
  width: "1px",
  height: "1px",
  padding: 0,
  overflow: "hidden",
  // Avoids the negative-margin "visually hidden" trick, which inflates scroll
  // height inside ancestors with overflow: auto (e.g. the dialog paper).
  clipPath: "inset(50%)",
  whiteSpace: "nowrap",
  border: 0,
};

export interface DialogProps extends Omit<MuiDialogProps, "content"> {
  title?: string;
  /**
   * Accessible name announced to screen readers when no visible `title` is rendered
   * (e.g. when the heading is displayed elsewhere in the dialog's content).
   */
  srOnlyTitle?: string;
  /**
   * The main content of the dialog. This can be any valid React node, such as text, form elements, or custom components.
   */
  content: ReactNode;
  /**
   * Optional actions to be displayed in the dialog's action area. If not provided, the action area will not be rendered.
   */
  actions?: ReactNode;
  /**
   * Indicates whether the close button (X) should be displayed in the top-right corner of the dialog.
   * @default true
   */
  showCloseButton?: boolean;
}

export function Dialog({
  title,
  srOnlyTitle,
  content,
  actions,
  showCloseButton = true,
  onClose,
  ...props
}: DialogProps) {
  return (
    <MuiDialog
      onClose={onClose}
      sx={{
        ".MuiDialog-paper": {
          maxWidth: "445px",
          p: 0,
        },
      }}
      {...props}
    >
      <DialogTitle>
        {title && (
          <Typography variant="body1" color="primary.main">
            {title}
          </Typography>
        )}
        {!title && srOnlyTitle && (
          <Typography sx={VISUALLY_HIDDEN_SX}>{srOnlyTitle}</Typography>
        )}
        {showCloseButton && (
          <IconButton
            onClick={(event) => {
              onClose?.(event, "backdropClick");
            }}
            aria-label="cerrar diálogo"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <Close />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent>{content}</DialogContent>

      {actions && <DialogActions>{actions}</DialogActions>}
    </MuiDialog>
  );
}
